import React, {
	type ReactNode,
	type ReactElement,
	type SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { styled } from '@compiled/react';
import Button, { ButtonGroup } from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useFeedbackCollector } from '@atlassian/jira-business-feedback-collector';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	SCREEN,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import {
	createDownloadLogsUrl,
	Status,
	useExperienceTracker,
	useMigrationIdManager,
	usePollMigrationStatus,
	useErrorLogFileName,
} from '@atlassian/jira-project-create-importer-common';

import { useCloudId, useTenantContext } from '@atlassian/jira-tenant-context-controller';
import ImportError from '../../common/assets/ImportErrorIllustration.svg';
import ImportInProgress from '../../common/assets/ImportInProgressIllustration.svg';
import { APP_IDENTIFIER, CONTACT_SUPPORT_URL, PROJECT_ROUTE_PREFIX } from '../../common/constants';
import type { SupportedThirdParty } from '../../common/constants/import-type';

import { EXPERIENCES, X2C_SCREENS } from '../../common/constants/metrics';
import type { OnDrawerClose } from '../../common/types';
import { CloseAndReturnButton } from '../../common/ui/close-and-return-button';
import { ProgressBarStandard, StepProgressBar } from '../../common/ui/step-progress-bar';
import { useContentForThirdParty, useUrlForThirdParty } from '../../common/utils';
import { useAnalyticsAttributesContext } from '../../common/utils/analtics-attributes-context';
import { useProjectSpecificContent } from '../../common/utils/project-specific-content';
import { useMigrationId, useProjectData } from '../../controllers/import-state';
import { useSource } from '../../controllers/import-state/selectors';
import { useProductSteps } from '../../controllers/utils';

import { CrossButton } from './cross-button';
import { ImportStepProgress } from './import-step-progress';
import messages from './messages';

const BASE_POLLING_INTERVAL_MS = 1000 * 2;
const ERROR_HEADING_ID = 'import-error';
const IN_PROGRESS_HEADING_ID = 'import-in-progress';
const FEEDBACK_GROUP_ID = 'x2c-post-import-feedback';

export const navigateToProjectPage = (url: string) => {
	window.location.href = url;
};

export type ImportInProgressScreenProps = {
	onCloseAndReturnToUploadStep: () => void;
	hasProgressBar?: boolean;
	hasImportUsers?: boolean;
	thirdParty?: SupportedThirdParty;
	showCrossButton?: boolean;
	isCompact?: boolean;
	onDrawerClose?: OnDrawerClose | null;
};

export const ImportInProgressScreen = ({
	hasProgressBar = false,
	hasImportUsers = true,
	thirdParty,
	showCrossButton = false,
	isCompact = false,
	onCloseAndReturnToUploadStep,
	onDrawerClose,
}: ImportInProgressScreenProps) => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const { isSiteAdmin } = useTenantContext();
	const [migrationId, { setMigrationId, setMappingState, setUploadedFileName }] = useMigrationId();
	const { removeMigrationId } = useMigrationIdManager();
	const [{ key, style, name }] = useProjectData();
	const errorLogFileName = useErrorLogFileName(name);
	const analyticsAttributes = useAnalyticsAttributesContext();
	const {
		migrationStatus: {
			overallStatus,
			detailedProgress: { dataTransformStatus, userImportStatus, projectCreateStatus },
			hasErrorLogs,
		},
	} = usePollMigrationStatus(migrationId, BASE_POLLING_INTERVAL_MS);
	const [onExperienceStart, onExperienceSuccess, onExperienceFail] = useExperienceTracker(
		EXPERIENCES.IMPORT_PART_1,
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [source] = useSource();
	const { productSteps, currentStepIndex } = useProductSteps();
	const { importStep1Description, rateLimitWarning } = useContentForThirdParty(thirdParty);
	const { rateLimitURL } = useUrlForThirdParty(thirdParty);
	const [{ type }] = useProjectData();
	const {
		importInProgressHeading,
		importInProgressDescription,
		importInProgressProjectStep,
		importInProgressErrorHeading,
		closeButtonLabel,
		importInProgressTroubleshootLink,
	} = useProjectSpecificContent(type, source);

	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
	const openFeedbackModal = useFeedbackCollector();
	const onSubmitFeedbackClick = useCallback(() => {
		const uiEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'clickX2CPostImportFeedback',
		});
		fireUIAnalytics(uiEvent);
		setIsFeedbackModalOpen(true);
		openFeedbackModal(FEEDBACK_GROUP_ID, () => setIsFeedbackModalOpen(false));
	}, [createAnalyticsEvent, openFeedbackModal]);

	const resetStore = useCallback(() => {
		setMigrationId(null);
		setMappingState(null);
		setUploadedFileName(null);
	}, [setMappingState, setMigrationId, setUploadedFileName]);

	const handleStartNewImport = useCallback(() => {
		const uiEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'clickX2CStartNewImport',
		});
		fireUIAnalytics(uiEvent);
		onCloseAndReturnToUploadStep();
	}, [createAnalyticsEvent, onCloseAndReturnToUploadStep]);

	const handleDownloadLogsClick = useCallback(() => {
		const uiEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'downloadX2CLogsLink',
		});
		fireUIAnalytics(uiEvent);
	}, [createAnalyticsEvent]);

	const handleOnCloseImportWindow = useCallback(
		(e: SyntheticEvent<HTMLElement>) => {
			const uiEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'closeX2CImportWindow',
			});
			fireUIAnalytics(uiEvent);
			onDrawerClose && onDrawerClose(e, uiEvent);
		},
		[createAnalyticsEvent, onDrawerClose],
	);

	const hasFailed =
		overallStatus === Status.FAILED ||
		dataTransformStatus === Status.FAILED ||
		userImportStatus === Status.FAILED ||
		projectCreateStatus === Status.FAILED;

	useEffect(() => {
		onExperienceStart();
	}, [onExperienceStart]);

	useEffect(() => {
		if (
			(projectCreateStatus === Status.SUCCESS || overallStatus === Status.SUCCESS) &&
			(!fg('enable-x2c-standardised-flow') || !isFeedbackModalOpen)
		) {
			resetStore();
			onExperienceSuccess({
				migrationId,
				thirdParty: thirdParty || '',
				...analyticsAttributes,
			});
			/*
			 * Redirect to newly created project
			 * Note: using react router push does not close project create draw.
			 */
			navigateToProjectPage(`${PROJECT_ROUTE_PREFIX}/${key}`);
		}
	}, [
		key,
		onExperienceSuccess,
		overallStatus,
		projectCreateStatus,
		analyticsAttributes,
		resetStore,
		migrationId,
		thirdParty,
		isFeedbackModalOpen,
	]);

	const getErrorMessage = useCallback(() => {
		if (dataTransformStatus === Status.FAILED) return 'Data Transform Failed';
		if (userImportStatus === Status.FAILED) return 'User Import Failed';
		if (projectCreateStatus === Status.FAILED) return 'Project Create Failed';

		return 'Overall Import Failed';
	}, [dataTransformStatus, projectCreateStatus, userImportStatus]);

	useEffect(() => {
		if (hasFailed) {
			onExperienceFail(
				APP_IDENTIFIER,
				{ name: 'Import Failed', message: getErrorMessage() },
				{ migrationId, thirdParty: thirdParty || '', ...analyticsAttributes },
			);
			removeMigrationId(key);
		}
	}, [
		analyticsAttributes,
		getErrorMessage,
		hasFailed,
		key,
		migrationId,
		onExperienceFail,
		removeMigrationId,
		style,
		thirdParty,
	]);

	const progressBarComponent = useMemo(() => {
		if (fg('enable-x2c-standardised-flow')) {
			return (
				<>
					{hasProgressBar && (
						<>
							<ProgressBarStandard
								productSteps={productSteps}
								currentStepIndex={currentStepIndex}
								onClose={onCloseAndReturnToUploadStep}
								showCrossButton={showCrossButton}
							/>
							<Box xcss={paddingForProgressBar} />
						</>
					)}
				</>
			);
		}
		return (
			<>
				{showCrossButton && (
					<CrossButton
						onClose={onCloseAndReturnToUploadStep}
						label={formatMessage(closeButtonLabel)}
					/>
				)}
				{hasProgressBar && (
					<StepProgressBar productSteps={productSteps} currentStepIndex={currentStepIndex} />
				)}
			</>
		);
	}, [
		showCrossButton,
		onCloseAndReturnToUploadStep,
		formatMessage,
		closeButtonLabel,
		hasProgressBar,
		productSteps,
		currentStepIndex,
	]);

	if (hasFailed) {
		return (
			<ErrorScreenWrapper isCompact={isCompact}>
				{showCrossButton ? (
					<CrossButton
						onClose={onCloseAndReturnToUploadStep}
						label={formatMessage(closeButtonLabel)}
					/>
				) : (
					<CloseAndReturnButton
						shouldShowCancelModal={false}
						onCloseAndReturnToUploadStep={onCloseAndReturnToUploadStep}
					/>
				)}
				<Wrapper isCompact={isCompact}>
					<Illustration src={ImportError} aria-describedby={ERROR_HEADING_ID} role="img" alt="" />
					<Heading id={ERROR_HEADING_ID}>{formatMessage(importInProgressErrorHeading)}</Heading>
					{migrationId && hasErrorLogs ? (
						<>
							<ErrorDescription>
								{formatMessage(messages.advanceErrorDesc, {
									br: <br />,
									link: (children): ReactElement => (
										<a href={CONTACT_SUPPORT_URL} target="_blank">
											{children}
										</a>
									),
								})}
							</ErrorDescription>
							<ButtonWrapper>
								<TroubleshootButton href={importInProgressTroubleshootLink} target="_blank">
									{formatMessage(messages.viewTroubleshootingPage)}
								</TroubleshootButton>
								<Button
									appearance="primary"
									onClick={handleDownloadLogsClick}
									href={createDownloadLogsUrl({
										cloudId,
										migrationId,
										fileName: errorLogFileName,
									})}
									download={errorLogFileName}
								>
									{formatMessage(messages.downloadLog)}
								</Button>
							</ButtonWrapper>
						</>
					) : (
						<>
							<ErrorDescription>
								{formatMessage(messages.errorDescription, { br: <br /> })}
							</ErrorDescription>
							<ContactSupportButton appearance="primary" href={CONTACT_SUPPORT_URL} target="_blank">
								{formatMessage(messages.contactSupport)}
							</ContactSupportButton>
						</>
					)}
				</Wrapper>
			</ErrorScreenWrapper>
		);
	}

	return (
		<ContextualAnalyticsData
			sourceName={X2C_SCREENS.IMPORT_IN_PROGRESS}
			sourceType={SCREEN}
			attributes={{ thirdParty: thirdParty || '' }}
		>
			<Wrapper
				data-testid="project-create-importer-upload-flow.ui.import-in-progress-screen.wrapper"
				hasProgressBar={hasProgressBar}
				isCompact={isCompact}
			>
				{progressBarComponent}
				<Illustration
					src={ImportInProgress}
					aria-describedby={IN_PROGRESS_HEADING_ID}
					role="img"
					alt=""
				/>
				<Heading id={IN_PROGRESS_HEADING_ID}>{formatMessage(importInProgressHeading)}</Heading>
				<Description>{formatMessage(importInProgressDescription, { br: <br /> })}</Description>
				<DetailsWrapper>
					<ImportStepProgress
						status={dataTransformStatus}
						description={
							thirdParty ? importStep1Description : formatMessage(messages.verifyingCsvData)
						}
					/>
					{hasImportUsers && isSiteAdmin && (
						<ImportStepProgress
							status={userImportStatus}
							description={formatMessage(messages.userImport)}
						/>
					)}
					<ImportStepProgress
						status={projectCreateStatus}
						description={formatMessage(importInProgressProjectStep)}
					/>
				</DetailsWrapper>
				{rateLimitWarning && (
					<Box xcss={sectionMessageStyles}>
						<SectionMessage testId="project-create-importer-upload-flow.ui.import-in-progress-screen.rate-limit-warning">
							<p>
								{formatMessage(rateLimitWarning, {
									link: (chunks: ReactNode) => (
										<a href={rateLimitURL} target="_blank">
											{chunks}
										</a>
									),
								})}
							</p>
						</SectionMessage>
					</Box>
				)}
				{fg('enable-x2c-standardised-flow') && (
					<>
						<ButtonGroup>
							<Box xcss={startNewImportStyles}>
								<Button
									onClick={handleStartNewImport}
									appearance="primary"
									testId="project-create-importer-upload-flow.ui.import-in-progress-screen.start-new-import-button"
								>
									{formatMessage(messages.startNewImport)}
								</Button>
							</Box>
							{onDrawerClose && (
								<Button
									testId="project-create-importer-upload-flow.ui.import-in-progress-screen.close-import-button"
									onClick={handleOnCloseImportWindow}
								>
									{formatMessage(messages.closeImportWindow)}
								</Button>
							)}
						</ButtonGroup>
						<Box xcss={paddingForFeedbackModal}>
							<Button
								testId="project-create-importer-upload-flow.ui.import-in-progress-screen.feedback-button"
								appearance="link"
								spacing="compact"
								onClick={onSubmitFeedbackClick}
							>
								{formatMessage(messages.submitFeedbackCallToAction)}
							</Button>
						</Box>
					</>
				)}
				<FireScreenAnalytics />
			</Wrapper>
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorScreenWrapper = styled.div<{ isCompact: boolean }>({
	display: 'flex',
	flexFlow: 'column',
	height: '100%',
	width: '100%',
	boxSizing: 'border-box',
	padding: `${token('space.300', '24px')} ${token('space.1000', '80px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isCompact }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		isCompact ? 'inherit' : token('color.background.input', colors.N0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		width: 'fit-content',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div<{ hasProgressBar?: boolean; isCompact: boolean }>({
	display: 'flex',
	flexFlow: 'column',
	alignItems: 'center',
	width: '100%',
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingTop: ({ hasProgressBar }) =>
		hasProgressBar ? token('space.600', '48px') : token('space.1000', '80px'),
	paddingBottom: token('space.300', '24px'),
	paddingLeft: token('space.1000', '80px'),
	paddingRight: token('space.1000', '80px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isCompact }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		isCompact ? 'inherit' : token('color.background.input', colors.N0),
	// progress bar margin bottom
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'> ul:first-of-type': {
		marginBottom: token('space.600', '48px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Illustration = styled.img({
	maxWidth: '408px',
	maxHeight: '288px',
	width: '50%',
	height: '50%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Heading = styled.h1({
	fontWeight: 600,
	fontSize: '20px',
	lineHeight: '32px',
	marginTop: token('space.200', '16px'),
	marginBottom: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.span({
	textAlign: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DetailsWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	marginTop: token('space.300', '24px'),
	marginBottom: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorDescription = styled.span({
	textAlign: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div({
	display: 'flex',
	marginTop: token('space.500', '40px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TroubleshootButton = styled(Button)({
	marginRight: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContactSupportButton = styled(Button)({
	marginTop: token('space.300', '24px'),
});

const paddingForProgressBar = xcss({
	paddingTop: token('space.1000', '80px'),
});

const paddingForFeedbackModal = xcss({
	paddingTop: token('space.200', '16px'),
});

const startNewImportStyles = xcss({
	marginRight: token('space.075', '6px'),
});

const sectionMessageStyles = xcss({
	paddingBottom: token('space.200', '16px'),
});
